import { modals } from '@mantine/modals';
import { IconCashPlus } from '@tabler/icons-react';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import { iconProps } from 'components/icon-props';
import { tKeys, useTranslate } from 'locales/index';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useCanUseManualActions } from 'shared/utils/connector';
import type { ActionButtonDefinition } from '../use-build-action-buttons';
import { computeCommonProps, useBatchAction } from './common';
import { openUnhandledInvoiceSelectionModal } from './open-unhandled-invoice-selection-modal';

export function useMarkAsPaid(): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const t = useTranslate();
  const batchAction = useBatchAction();
  const { selectedInvoiceIds } = useInvoiceSelection();
  // Payments are owned by the accounting integration once one is connected; marking
  // an invoice paid by hand would drift from what gets synced back. Same gate as the
  // invoice listing's batch actions.
  const hasManualActionsActive = useCanUseManualActions();

  return (action, invoices) => {
    // Confirm/refine the invoice selection before marking them as paid
    const handler = () =>
      openUnhandledInvoiceSelectionModal({
        title: t(tKeys.DEBTOR.REACTION.SELECT_INVOICES),
        selectedInvoiceIds,
        invoices,
        actionType: 'mark_as_paid',
        isFinalStep: true,
        onNext: ({ invoiceIds }) => {
          modals.closeAll();
          batchAction('mark_as_paid', invoiceIds, {});
        },
      });

    const commonProps = computeCommonProps(action, invoices);

    return {
      icon: <IconCashPlus {...iconProps()} />,
      translationKey: tKeys.MARK_AS_PAID,
      onClick: handler,
      ...commonProps,
      isEnabled: commonProps.isEnabled && hasManualActionsActive,
      disabledReason: hasManualActionsActive
        ? undefined
        : t(tKeys.MODULES.AT_RISK.MARK_AS_PAID_SYNCED),
    };
  };
}
